import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'

interface ThemeState {
  isDark: boolean
  toggleTheme: () => void
  setTheme: (isDark: boolean) => void
  maskAmounts: boolean
  toggleMaskAmounts: () => void
}

export const useThemeStore = create<ThemeState>()(
  devtools(
    persist(
      (set) => ({
        isDark: false,
        maskAmounts: false,

        toggleMaskAmounts: () => set((state) => ({ maskAmounts: !state.maskAmounts })),

        toggleTheme: () => {
          set((state) => {
            const newTheme = !state.isDark
            updateDOMTheme(newTheme)
            return { isDark: newTheme }
          })
        },
        
        setTheme: (isDark: boolean) => {
          set({ isDark })
          updateDOMTheme(isDark)
        }
      }),
      {
        name: 'theme-store',
        onRehydrateStorage: () => (state) => {
          // Apply theme to DOM when store is rehydrated
          if (state) {
            updateDOMTheme(state.isDark)
          }
        }
      }
    ),
    {
      name: 'theme-store'
    }
  )
)

// Helper function to update DOM theme
function updateDOMTheme(isDark: boolean) {
  if (isDark) {
    document.documentElement.classList.add('dark')
    localStorage.setItem('theme', 'dark')
  } else {
    document.documentElement.classList.remove('dark')
    localStorage.setItem('theme', 'light')
  }
}

// Initialize theme from system preference or localStorage
const initializeTheme = () => {
  const savedTheme = localStorage.getItem('theme')
  const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
  
  let isDark = false
  
  if (savedTheme) {
    isDark = savedTheme === 'dark'
  } else {
    isDark = prefersDark
  }
  
  useThemeStore.getState().setTheme(isDark)
}

// Listen for system theme changes
window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
  // Only auto-switch if user hasn't manually set a preference
  const savedTheme = localStorage.getItem('theme')
  if (!savedTheme) {
    useThemeStore.getState().setTheme(e.matches)
  }
})

// Initialize theme on store creation
initializeTheme()